/**
 * Канонический контент: одна zod-схема, один тип, одна форма. §Хребет данных 2.
 *
 * Формы-конкуренты удалены: дискриминированный union по `kind` с парой text/caption
 * (02-contracts.md §4.2) и массив media (06-signer.md §1.1).
 *
 * Порядок полей нормативен и совпадает с порядком кодирования (§Хребет данных 4.2):
 *   cv, method, text, entities, link_preview_options, media, document
 * Порядок полей дескриптора медиа: mime_type, sha256, bytes, show_caption_above_media, has_spoiler
 * Порядок полей дескриптора документа: mime_type, sha256, bytes, file_name
 *
 * Имена полей — ТОЧНО как в Bot API, snake_case, дословно. Слоя маппинга нет.
 */

import * as z from 'zod';
import { HUMAN_DOCUMENT_MAX_BYTES } from '../media.ts';
import { codePointLen, limitValue, SEND_MESSAGE_MAX_CODEPOINTS, Utf16Units } from '../units.ts';
import { CANON_ALGO_VERSION } from '../version.ts';

/**
 * Адрес под анкором. ТОЛЬКО `https://` — решение Ильи 25.08 (`docs/PHASE-2.md` §2.1),
 * ПРАВИЛО C3-2 `docs/phase-2/06-security-ops.md`.
 *
 * До этой правки схема принимала ещё `http://` и `tg://`, и это было безопасно ровно
 * потому, что тексты фазы 1 писали мы сами. В фазе 2 текст пишет модель, прочитавшая
 * открытый интернет, и `tg://resolve?domain=<чужой_бот>&start=<payload>` — это открытие
 * чужого бота с параметром прямо из канала бренда, которое согласующий видит как шум,
 * а не как угрозу. `http://` уходит той же правкой: адрес без TLS в посте бренда не
 * нужен ни для чего, а различать «безопасный http» нечем.
 *
 * Схема закрывает половину политики — ту, что не зависит от контекста. Вторая половина
 * (какому ХОСТУ адрес принадлежит) контекстна и живёт в `checkLinkPolicy` ниже.
 */
const HTTPS_SCHEME_RE = /^https:\/\//i;

/**
 * Единственное место, где записано правило схемы. Редактор исходной разметки
 * (`telegram/source-markup.ts`) и канонизация (`canonical/canonicalize.ts`) спрашивают
 * его же: разойдясь, они заверяют разметку, которую заморозка потом отвергает (F-091).
 */
export function isTelegramUrl(url: string): boolean {
  return url.length > 0 && HTTPS_SCHEME_RE.test(url);
}

export const TelegramUrl = z
  .string()
  .min(1)
  .refine((u) => isTelegramUrl(u), { error: 'url must be https://' });

const base = { offset: Utf16Units, length: z.int().positive().brand<'utf16'>() };

export const MessageEntitySchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('bold'), ...base }),
  z.strictObject({ type: z.literal('italic'), ...base }),
  z.strictObject({ type: z.literal('underline'), ...base }),
  z.strictObject({ type: z.literal('strikethrough'), ...base }),
  z.strictObject({ type: z.literal('spoiler'), ...base }),
  z.strictObject({ type: z.literal('code'), ...base }),
  z.strictObject({
    type: z.literal('pre'),
    ...base,
    language: z
      .string()
      .regex(/^[A-Za-z0-9+#._-]{1,32}$/)
      .optional(),
  }),
  z.strictObject({ type: z.literal('text_link'), ...base, url: TelegramUrl }),
  z.strictObject({ type: z.literal('blockquote'), ...base }),
  z.strictObject({ type: z.literal('expandable_blockquote'), ...base }),
]);
export type MessageEntity = z.output<typeof MessageEntitySchema>;

export const LinkPreviewOptionsSchema = z.strictObject({
  is_disabled: z.boolean(),
  /** null = «первая ссылка в тексте», дефолт Telegram. */
  url: z.string().nullable(),
  prefer_small_media: z.boolean(),
  prefer_large_media: z.boolean(),
  show_above_text: z.boolean(),
});
export type LinkPreviewOptions = z.output<typeof LinkPreviewOptionsSchema>;

export const MediaDescriptorSchema = z.strictObject({
  mime_type: z.enum(['image/png', 'image/jpeg']),
  /** sha256 БАЙТОВ файла, 64 символа [0-9a-f]. */
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  /** Точный размер файла в байтах. Ловит обрезанный файл до хэширования. */
  bytes: z.int().positive(),
  show_caption_above_media: z.boolean(),
  has_spoiler: z.boolean(),
});
export type MediaDescriptor = z.output<typeof MediaDescriptorSchema>;

/**
 * Имя документа, под которым его увидит читатель канала: 1–128 кодпоинтов, без разделителей
 * пути и управляющих символов. Тот же потолок стоит CHECK'ом колонки `media_asset.file_name`
 * (миграция 0020): SQLite считает `length()` в символах, то есть в кодпоинтах.
 */
export const DOCUMENT_FILE_NAME_MAX = 128;
// biome-ignore lint/suspicious/noControlCharactersInRegex: управляющие символы здесь и запрещаются
const DOCUMENT_FILE_NAME_RE = /^[^/\\\u0000-\u001f\u007f]+$/u;

export function isDocumentFileName(name: string): boolean {
  return (
    name.isWellFormed() &&
    DOCUMENT_FILE_NAME_RE.test(name) &&
    codePointLen(name) <= DOCUMENT_FILE_NAME_MAX &&
    name.trim() === name
  );
}

/**
 * Документ внизу поста (docs/release-2026-09-19.md §9.2) — пока только PDF. Как и медиа, в
 * подписываемое содержание входит ДЕСКРИПТОР, а не адрес байтов: `sha256` и точный размер
 * ловят подмену и обрезку файла, имя — то, что человек утвердил. Документ бывает только у
 * rich-сообщения: у `sendMessage` нет места для файла, а отдельное сообщение с файлом было бы
 * вторым постом.
 */
export const DocumentDescriptorSchema = z.strictObject({
  mime_type: z.literal('application/pdf'),
  /** sha256 БАЙТОВ файла, 64 символа [0-9a-f]. */
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  bytes: z.int().positive().max(HUMAN_DOCUMENT_MAX_BYTES),
  file_name: z
    .string()
    .refine((n) => isDocumentFileName(n), { error: 'document_file_name_invalid' }),
});
export type DocumentDescriptor = z.output<typeof DocumentDescriptorSchema>;

export const DraftContentSchema = z
  .strictObject({
    cv: z.literal(CANON_ALGO_VERSION),
    /**
     * Решение Ильи 06.09.2026 (docs/phase-2/08-visuals.md §1.2ж), проверено живыми пробами:
     *   sendMessage     — текст ≤ 4064 кодпоинтов, нет ни картинки, ни документа; карточка
     *                     превью снизу;
     *   sendRichMessage — всё остальное; карточки не бывает вовсе — параметра
     *                     `link_preview_options` у метода не существует.
     * С 19.09.2026 метод ВЫБИРАЕТСЯ при заморозке (`freezeAuthored`, политика бренда «все
     * посты — расширенным сообщением») и дальше ЧИТАЕТСЯ отсюда, а не выводится заново:
     * короткий пост без картинки законно бывает и тем и другим.
     * `sendPhoto` не отправляется больше никогда, но код 2 в METHOD_CODE
     * (canonical/encode.ts) остаётся ЗАРЕЗЕРВИРОВАННЫМ навсегда: иначе строка в базе со
     * старым значением перестала бы разбираться. То же правило, что у ENTITY_TYPE_CODE.
     */
    method: z.enum(['sendMessage', 'sendRichMessage']),
    /** У поста с картинкой это по-прежнему один и тот же текст. Одно поле на все методы. */
    text: z.string(),
    entities: z.array(MessageEntitySchema),
    link_preview_options: LinkPreviewOptionsSchema.nullable(),
    media: MediaDescriptorSchema.nullable(),
    /**
     * PDF внизу поста (§9.2). Необязательное поле транспортной формы: содержание, заведённое
     * до 19.09.2026, его не несёт и разбирается как `null` — и кодируется без единого нового
     * байта, то есть его хэш не меняется (`canonical/encode.ts`).
     */
    document: DocumentDescriptorSchema.nullable().default(null),
  })
  .check((ctx) => {
    const v = ctx.value;
    // Законных форм три, и описывают их ровно две проверки:
    //   sendMessage     + media === null + lpo !== null
    //   sendRichMessage + media !== null + lpo === null
    //   sendRichMessage + media === null + lpo === null   (длинный текст, §1.2ж)
    //
    // ИМПЛИКАЦИЯ, а не биусловие: прежнее `method ⟺ media` держалось только потому, что
    // методов было два. Теперь sendRichMessage покрывает и пост с картинкой, и длинный
    // пост без неё, и обратная сторона биусловия стала ложной.
    if (v.media !== null && v.method !== 'sendRichMessage') {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['media'],
        message: 'method_media_mismatch',
      });
    }
    // А это биусловие не ослабляется: ловит и «забыли опции у текстового поста» (§5.1 —
    // карточка задаётся всегда явно), и «прислали опции у rich-поста».
    if ((v.method === 'sendMessage') !== (v.link_preview_options !== null)) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['link_preview_options'],
        message: 'lpo_method_mismatch',
      });
    }
    // Документ — только блоком rich-сообщения: у `sendMessage` места для файла нет.
    if (v.document !== null && v.method !== 'sendRichMessage') {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['document'],
        message: 'method_document_mismatch',
      });
    }
  });

export type DraftContent = z.output<typeof DraftContentSchema>;

/**
 * Порождается ТОЛЬКО canonicalize(). Схема бренд не ставит — иначе parse() выдавал бы
 * неканоничное как каноничное.
 *
 * Явный `as CanonicalDraft` компилируется, поэтому он запрещён ТЕКСТОВО: правило R12
 * стража границ отбивает его везде, кроме canonical/canonicalize.ts. Без R12 бренд —
 * украшение.
 */
export type CanonicalDraft = DraftContent & z.core.$brand<'canonical'>;

/** Есть ли у поста картинка. Раньше это спрашивали у метода — метод больше не отвечает. */
export function hasMedia(c: Pick<DraftContent, 'media'>): boolean {
  return c.media !== null;
}

/** Политика транспорта бренда в момент заморозки версии (§9.3). */
export interface WireMethodOptions {
  /** «Все посты — расширенным сообщением»: короткий пост без картинки тоже уходит rich. */
  readonly richAlways?: boolean;
}

/**
 * КАКОЙ МЕТОД ВЫБРАТЬ ПРИ ЗАМОРОЗКЕ. Решение Ильи 06.09.2026 (`08-visuals.md` §1.2ж):
 * картинка или документ есть ЛИБО текст длиннее порога — `sendRichMessage`, иначе
 * `sendMessage`; с политикой бренда «все посты — расширенным сообщением» (19.09.2026) —
 * `sendRichMessage` всегда.
 *
 * С 19.09.2026 это правило ВЫБОРА, а не вывода: зовёт его заморозка (`freezeAuthored`), а
 * сборка запроса Bot API и сверка после публикации читают `method` подписанного
 * содержания. Выводить метод заново на проводе нельзя: при включённой политике короткий
 * пост законно уходит rich, и вывод по длине отправил бы `sendMessage` без карточки.
 *
 * Считается по КОДПОИНТАМ и по КАНОНИЧЕСКОМУ тексту. По сырому нельзя: черновик в 4070
 * знаков с хвостовыми пробелами канонизируется в 4060, и решение по сырому отправило бы
 * его rich-сообщением, молча отняв карточку превью.
 */
export function wireMethodOf(
  c: Pick<DraftContent, 'text' | 'media'> & {
    readonly document?: DraftContent['document'] | undefined;
  },
  opts: WireMethodOptions = {},
): DraftContent['method'] {
  if (opts.richAlways === true) return 'sendRichMessage';
  if (c.media !== null || (c.document ?? null) !== null) return 'sendRichMessage';
  return codePointLen(c.text) > limitValue(SEND_MESSAGE_MAX_CODEPOINTS)
    ? 'sendRichMessage'
    : 'sendMessage';
}

// ---------------------------------------------------------------------------
// Подпись первоисточника
// ---------------------------------------------------------------------------

/**
 * Текст машинной подписи первоисточника — целиком анкор (`text_link`) на `url_canonical`
 * ведущего материала темы, отдельным блоком в конце поста. Дописывает её сборка черновика
 * у форматов с `source_link: 'footer'` (`api/knowledge.ts`), а не модель: решение Ильи
 * 2026-09-08, та же механизация, что у жирного лида.
 *
 * ЛИТЕРАЛ ЖИВЁТ ЗДЕСЬ, потому что читателей у него три и все три обязаны видеть ОДНУ строку:
 *   * писатель — он эту строку пишет и считает по ней смещение `text_link` (в code units
 *     UTF-16, длина подписи и есть `length` сущности);
 *   * интерфейс — превью обязано показать ровно то, что уедет в канал, а идемпотентность
 *     дописки («текст уже кончается подписью») проверяется сравнением С ЭТОЙ СТРОКОЙ;
 *   * тесты обеих сторон — сверка `content_hash` замороженного текста.
 * Разъехавшись на один символ, они дали бы вторую подпись на каждой переделке и другой
 * `content_hash` у текста, который обязан остаться байт-в-байт.
 *
 * Анкор, а не голый адрес, выбран сознательно: над анкором сервер Telegram своих сущностей
 * не добавляет, и сверка §3.1а `docs/telegram.md` при публикации не шелохнётся.
 */
export const SOURCE_LINK_LABEL = 'Источник';

/**
 * Адрес-заглушка ссылки ВНУТРИ текста — у форматов с `source_link: 'inline'` (AGI Boardroom,
 * решение Ильи 2026-11-02). Модель ставит ровно одну ссылку диалекта `[глагол](адрес-заглушка)`
 * на глаголе действующего лица в первой фразе новости, а сборка черновика заменяет адрес на
 * `url_canonical` официального первоисточника темы — или снимает ссылку, оставив слово, если
 * первоисточника нет. Модель настоящего адреса не пишет никогда: место ссылки — решение языка,
 * адрес — решение данных.
 *
 * ПОЧЕМУ `.invalid`. Зона RFC 2606 не резолвится нигде (`RESERVED_LINK_TLDS`, правило ссылок):
 * заглушка, пережившая сборку по любой ошибке, отбивается правилом C3-1 на обеих сторонах и не
 * уходит в канал мёртвой ссылкой. Схема — `https://`, чтобы разметка разбиралась
 * (`isTelegramUrl`) и ошибка всплывала правилом ссылок с адресом, а не отказом разбора без него.
 *
 * ЛИТЕРАЛ ЖИВЁТ ЗДЕСЬ по той же причине, что и подпись выше: его знают база знаний (как писать
 * ссылку), сборка писателя (что заменять) и тесты обеих сторон, и разойтись на символ они не
 * должны — иначе сборка не узнала бы заглушку и оставила её в посте.
 */
export const SOURCE_LINK_PLACEHOLDER_URL = 'https://source.invalid/';

// ---------------------------------------------------------------------------
// Политика внешних ссылок — ПРАВИЛО C3-1
// ---------------------------------------------------------------------------

/**
 * ОТКУДА БЕРЁТСЯ СПИСОК РАЗРЕШЁННЫХ ХОСТОВ. Источников два, и они обязаны остаться двумя,
 * потому что у них разное время жизни:
 *
 *   1. **Домены бренда** — `config/brands/<brand>.yaml`, секция `links.allowed_hosts`
 *      (`api/brand-config.ts`). Статика: пишет человек, читается один раз при старте,
 *      одинакова для всех черновиков бренда.
 *   2. **Домен первоисточника темы** — динамика: известен ТОЛЬКО когда у черновика есть
 *      тема, и берётся из ведущего материала темы (`topic_source_ref.role='primary'` →
 *      `source_material.url_canonical`). У фикстуры и у черновика, заведённого руками,
 *      темы нет, и второе слагаемое пусто — тогда действуют одни домены бренда.
 *
 * Отсюда форма этого модуля. Хост — свойство пары «черновик + его тема», а не строки, и
 * zod-схема его проверить не может: контекста у `refine` нет. Поэтому схема делает
 * бесконтекстную половину (`TelegramUrl` — только `https://`), а контекстную половину
 * делает чистая функция, которой РАЗРЕШЁННЫЙ НАБОР ПЕРЕДАЁТ вызывающий. Собирает набор
 * валидатор версии на приёме (`intake`): он один видит и конфиг бренда, и тему.
 *
 * Отдельного списка «доверенных доменов вообще» не заводится сознательно: список, который
 * не привязан ни к бренду, ни к теме, растёт от каждой удобной ссылки и через полгода
 * разрешает всё.
 *
 * ЧЕГО ЭТА ФУНКЦИЯ НЕ ВИДИТ, и это обязано быть закрыто вызывающим: голую ссылку БЕЗ
 * схемы (`geoboost.ru/blog`). Telegram линкует её сам, но воспроизводит это только детектор
 * сервера, а его импорт из основного входа запрещён (§Хребет 6, вход `@smm/contracts/preview`).
 * Валидатор на приёме обязан прогнать `expectedServerEntities` и подать сюда найденные
 * адреса — ровно тем же `isHostAllowed`.
 */

const HTTPS_PREFIX_RE = /^https:\/\//i;

/**
 * Хост адреса, приведённый к сравнимой форме: без схемы, без userinfo, без порта, в нижнем
 * регистре, без завершающей точки. Не `new URL()`: разбор в контрактах обязан быть чистым и
 * одинаковым во всех средах, а `URL` нормализует адрес по-своему (в том числе punycode) —
 * то есть решает за нас, а решение здесь блокирующее.
 *
 * Пустая строка означает «это не https-адрес». Возвращать сам адрес, как это делает
 * `hostOfUrl` в `preview/`, здесь нельзя: там результат идёт человеку в интерфейс, здесь —
 * в сравнение со списком, и адрес, случайно совпавший с записью списка, был бы дырой.
 */
export function linkHostOf(url: string): string {
  const m = HTTPS_PREFIX_RE.exec(url);
  if (m === null) return '';
  const rest = url.slice(m[0].length);
  const cut = rest.search(/[/?#]/);
  const authority = cut === -1 ? rest : rest.slice(0, cut);
  // lastIndexOf, а не indexOf: пароль в userinfo вправе содержать '@'.
  const at = authority.lastIndexOf('@');
  const hostPort = at === -1 ? authority : authority.slice(at + 1);
  // Литерал IPv6 несёт двоеточия внутри скобок — резать по первому ':' нельзя.
  const close = hostPort.startsWith('[') ? hostPort.indexOf(']') : -1;
  const host = close === -1 ? (hostPort.split(':')[0] ?? '') : hostPort.slice(0, close + 1);
  const lower = host.toLowerCase();
  return lower.endsWith('.') ? lower.slice(0, -1) : lower;
}

/**
 * Запись списка покрывает свои поддомены: `geoboost.ru` разрешает `blog.geoboost.ru`.
 * Иначе список пришлось бы дописывать при каждом новом разделе сайта, а забытая дописка
 * выглядела бы как ложное срабатывание защиты — и защиту бы выключили.
 *
 * Записи списка обязаны быть уже в нижнем регистре: это утверждает схема конфига.
 */
export function isHostAllowed(host: string, allowedHosts: readonly string[]): boolean {
  if (host === '') return false;
  return allowedHosts.some((a) => host === a || host.endsWith(`.${a}`));
}

/** Где найден адрес — это и есть объяснение согласующему, что именно надо править. */
export type LinkSite = 'text_link' | 'link_preview' | 'bare_url';

export interface LinkViolation {
  readonly site: LinkSite;
  readonly url: string;
  /** Пустая строка = адрес не является `https://`-адресом вовсе. */
  readonly host: string;
}

/**
 * Голая ссылка со схемой. Тот же приём, что у `BARE_URL_RE` в `canonical/canonicalize.ts`
 * (поиск по схеме, а не детектором) и по той же причине — §Хребет 6.
 *
 * Сущности при этом НЕ учитываются: адрес внутри `code`/`pre` сервер не линкует, но читатель
 * его видит и копирует, а решение «этот адрес в посте бренда допустим» от кликабельности не
 * зависит. Список схем шире `https`: цель — найти адрес и отбить его, а не пропустить.
 *
 * Перед схемой стоит ОТРИЦАТЕЛЬНАЯ ретроспекция, а не список разделителей: детектор сервера
 * делает ссылкой `Источник:https://…` и любой другой адрес, приклеенный к предыдущему
 * символу, а гейт со списком разделителей его не видел вовсе — M-004. Ретроспекция по
 * символам схемы оставляет нетронутым единственный случай, где `://` частью адреса не
 * является: продолжение уже найденной схемы.
 */
const BARE_URL_G = /(?<![A-Za-z0-9+.-])([A-Za-z][A-Za-z0-9+.-]*:\/\/[^\s)\]>»"']{3,})/g;

/**
 * ПРАВИЛО C3-1: каждый адрес на выходе — против списка. Возвращает нарушения, а не булево:
 * согласующему и журналу нужен адрес, а не «что-то не так».
 *
 * Пустой `allowedHosts` — законный вход и означает «внешних ссылок в этом посте быть не
 * может». Это не вырожденный случай, а рекомендация §9 В1 `06-security-ops.md` для бренда,
 * который решил не ссылаться наружу вовсе.
 */
export function checkLinkPolicy(
  content: Pick<DraftContent, 'text' | 'entities' | 'link_preview_options'>,
  allowedHosts: readonly string[],
): LinkViolation[] {
  const out: LinkViolation[] = [];
  const check = (site: LinkSite, url: string): void => {
    const host = linkHostOf(url);
    if (!isHostAllowed(host, allowedHosts)) out.push({ site, url, host });
  };

  for (const e of content.entities) {
    if (e.type === 'text_link') check('text_link', e.url);
  }

  const lpo = content.link_preview_options;
  // Схема поля `url` у превью — просто строка, и сужать её нельзя: `null` и `''` там
  // означают «дефолт Telegram, ссылку ищет сервер». Проверяем только заданный адрес.
  if (lpo !== null && lpo.url !== null && lpo.url !== '') check('link_preview', lpo.url);

  for (const m of content.text.matchAll(BARE_URL_G)) {
    const url = m[1];
    if (url !== undefined) check('bare_url', url);
  }

  return out;
}
